import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, LinearProgress, SxProps, Theme, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ExportButton } from 'shared/components/export-button';
import { CustomerContractInventoryGridItem, ContractRouteParams, CustomerLookup } from 'shared/types';
import { CustomerContractTable } from './tables/customer-contract-table';
import { useCustomerContractTableInstance } from './tables/customer-contract-table-instance';
import { useExportContractInventory } from 'shared/mutations';
import { ContractUploadDialog } from './contractUploadDialog';
import { ContractStatusDialog } from './contractStatusDialog';
import { ContractDeleteDialog } from './contractDeleteDialog';
import { useContractInventoryFormState } from 'shared/contexts/contract-inventory-form-context';
import { useQueryContracts, useQueryCustomerLookupWithFilters } from 'shared/queries';
import { mapContractsToContractInventoryGridItems, compareDates } from 'shared/utility';
import { useUserPermissions, useTitle } from 'shared/hooks';
import { PERMISSIONS } from 'shared/constants';
import { useCreateCustomer } from 'shared/mutations/create-customer';

const tableContainerStyles: SxProps<Theme> = {
  paddingTop: '1rem',
  overflow: 'auto',
  width: '100%',
};

const dataDisplayContentStyles: SxProps<Theme> = {
  overflowY: 'auto',
};

const contentSectionStyles: SxProps<Theme> = {
  alignItems: 'center',
  display: 'flex',
  flexFlow: 'column nowrap',
  justifyItems: 'center',
};

const secondaryContentSectionStyles: SxProps<Theme> = {
  ...contentSectionStyles,
  paddingTop: '1rem',
};

const tableTitleStyles: SxProps<Theme> = {
  display: 'flex',
  flexFlow: 'row',
  justifyContent: 'space-between',
  alignContent: 'flex-end',
  width: '100%',
};

const dataTableActionBarStyles: SxProps<Theme> = {
  display: 'flex',
  flexFlow: 'row',
  justifyContent: 'flex-end',
  alignContent: 'flex-end',
  width: '100%',
};

const tableTitleDisplayStyles: SxProps<Theme> = {
  justifyContent: 'center',
  display: 'flex',
  flexFlow: 'column',
};

const gridStyles: SxProps<Theme> = {
  display: 'flex',
  gap: '30px',
};

export function ContractSummary() {
  const { masterCustomer, abgBrand } = useParams<ContractRouteParams>();
  const { customers } = useQueryCustomerLookupWithFilters({
    channel: '',
    customerTier: '',
    abgBrand,
    masterCustomer,
  });
  const [customer, setCustomer] = useState<CustomerLookup | undefined>(undefined);
  useEffect(() => {
    if (!customer) {
      if (customers && customers.length > 0) {
        const rightCustomer = customers.find(
          c =>
            c.uniqueid.includes(`${masterCustomer}${abgBrand}`) &&
            c.brand === abgBrand &&
            c.abgmastercustomer === masterCustomer
        );
        if (rightCustomer && rightCustomer.brand === abgBrand && rightCustomer.abgmastercustomer === masterCustomer) {
          setCustomer(rightCustomer);
        }
      }
    }
  }, [customers, customer, setCustomer, abgBrand, masterCustomer]);

  const { contracts, isFetching, isLoading } = useQueryContracts({ brand: abgBrand, customer: masterCustomer });

  const userPermissions = useUserPermissions();
  const hasEditPermission = userPermissions.includes(PERMISSIONS['contract.edit']);
  const { toggleNewContractUploadDialog } = useContractInventoryFormState();
  const createCustomerMutation = useCreateCustomer('update');
  const loading = isLoading || isFetching;
  useTitle('Contract Summary - American Bath');

  useEffect(() => {
    if (customer && contracts) {
      const activeContractsLength = contracts.filter(
        c => c.status === 'active' && (!c.endDate || compareDates(new Date(c.endDate), new Date()) !== 1)
      ).length;
      if (parseInt(customer.numberofcontracts) !== activeContractsLength) {
        setCustomer({
          ...customer,
          numberofcontracts: activeContractsLength.toString(),
        });
        createCustomerMutation.mutate({
          ...customer,
          numberofcontracts: activeContractsLength.toString(),
        });
      }
    }
  }, [createCustomerMutation, customer, contracts]);

  const customerContractInventoryTableData: CustomerContractInventoryGridItem[] = useMemo(
    () => mapContractsToContractInventoryGridItems(contracts),
    [contracts]
  );

  const customerContractsTableInstance = useCustomerContractTableInstance(customerContractInventoryTableData);
  const customerContractsExportMutation = useExportContractInventory({ brand: abgBrand, customer: masterCustomer });

  const exportContracts = useCallback(() => {
    customerContractsExportMutation.mutate();
  }, [customerContractsExportMutation]);
  return (
    <>
      <Box sx={dataDisplayContentStyles}>
        <Grid item container xs={12} sx={secondaryContentSectionStyles}>
          <Grid item container xs={12} sx={tableTitleStyles}>
            <Grid sx={gridStyles} item xs={9}>
              <Typography sx={tableTitleDisplayStyles} variant='h5'>
                Contract Inventory
              </Typography>
              <Button
                variant='contained'
                color='secondary'
                startIcon={<AddCircleIcon />}
                disabled={!hasEditPermission}
                title={!hasEditPermission ? 'You do not have permission to perform this action' : ''}
                onClick={() => toggleNewContractUploadDialog()}
              >
                Add contract
              </Button>
              <ContractUploadDialog />
            </Grid>
            <Grid sx={gridStyles} item xs={3}>
              <Box sx={dataTableActionBarStyles}>
                <ExportButton noun='Contract List' onClick={exportContracts} />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={tableContainerStyles}>
          {loading && <LinearProgress color='secondary' />}
          <CustomerContractTable instance={customerContractsTableInstance} />
          <ContractStatusDialog />
          <ContractDeleteDialog />
        </Grid>
      </Box>
    </>
  );
}
